import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { Link } from 'react-router-dom';

import './stylesheet.css';

import {
  addConversation,
  addEncounter,
  addObservation,
  addPractice,
  COMMITMENT_LABELS,
  COMMITMENT_STATES,
  commitmentTally,
  conversationDue,
  DEFAULT_FORMATION_INTERVAL_DAYS,
  DEFAULT_FORMATION_PATH,
  DIMENSIONS,
  dimensionStatus,
  emptyFormationState,
  HONESTY_CARDS,
  openCommitment,
  practiceRecency,
  practisedOn,
  setDimensionNote,
} from '../../lib/formation';
import { extractFormationFromFile, upsertFormationNode } from '../../lib/formation_org';
import { directoryOfPath } from '../../lib/dashboard';

// How much of each append-only log the page shows at once. The file holds all of
// it; this is only about not turning the page into a wall.
const LOG_SHOWN = 6;

const EMPTY_ENCOUNTER = {
  who: '',
  said: '',
  felt: '',
  differently: '',
  avoided: '',
  dimension: null,
};

const EMPTY_OBSERVATION = { who: '', what: '' };
const EMPTY_CONVERSATION = { brought: '', named: '', committed: '', kept: null };

const FormationDashboard = ({ syncBackendClient, formationPath, intervalDays }) => {
  const path = (formationPath || '').trim() || DEFAULT_FORMATION_PATH;

  // Fixed at mount, exactly like the other four dashboards: a page left open
  // across midnight keeps dating its entries to the day it was opened on until
  // it is refreshed.
  const today = useMemo(() => new Date(), []);

  const [state, setState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [noteDraft, setNoteDraft] = useState(null); // { key, text }
  const [encounterDraft, setEncounterDraft] = useState(EMPTY_ENCOUNTER);
  const [observationDraft, setObservationDraft] = useState(EMPTY_OBSERVATION);
  const [conversationDraft, setConversationDraft] = useState(EMPTY_CONVERSATION);

  // The raw text of the file, so a write only ever replaces the formation node
  // and leaves the rest of the file exactly as it was. A ref, not state, so two
  // quick edits both build on the write that came just before them.
  const rawRef = useRef(null);
  const existsRef = useRef(false);
  // Chained so two quick edits become two orderly commits instead of two racing
  // ones - the GitLab commit API rejects concurrent branch updates.
  const queueRef = useRef(Promise.resolve());

  const load = useCallback(async () => {
    if (!syncBackendClient) {
      setLoadError('Not connected to a sync backend.');
      setLoaded(true);
      return;
    }

    setLoadError(null);
    setLoaded(false);

    try {
      // Listed first, like the other dashboards: knowing whether the file is
      // there avoids a failed fetch on a record that has not been started yet -
      // and stops a network error from being mistaken for "no file", which would
      // let the next write overwrite a file that does exist.
      const listing = await syncBackendClient
        .getDirectoryListing(directoryOfPath(path))
        .catch(() => null);

      const present = new Set();
      ((listing && listing.listing) || []).forEach((file) => {
        const filePath = file.get ? file.get('path') : file.path;
        if (filePath) present.add(filePath);
      });

      const contents = present.has(path) ? await syncBackendClient.getFileContents(path) : null;

      rawRef.current = contents;
      existsRef.current = contents !== null;

      setState(extractFormationFromFile(contents));
      setLoaded(true);
    } catch (error) {
      console.error('Could not read the formation record:', error);
      setLoadError(error.message);
      setState(null);
      setLoaded(true);
    }
  }, [syncBackendClient, path]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  const persist = useCallback(
    (nextState) => {
      setState(nextState);
      setSaveError(null);

      const nextContents = upsertFormationNode(rawRef.current, nextState);
      rawRef.current = nextContents;

      queueRef.current = queueRef.current.then(async () => {
        try {
          if (existsRef.current) {
            await syncBackendClient.updateFile(path, nextContents);
          } else {
            await syncBackendClient.createFile(path, nextContents);
            existsRef.current = true;
          }
        } catch (error) {
          setSaveError(`Could not save ${path}: ${error.message || error}`);
        }
      });
    },
    [syncBackendClient, path]
  );

  const canWrite = !!syncBackendClient;

  // A file with no formation node yet is shown as an empty record rather than as
  // a wall: the page is usable straight away, and the file is only created once
  // there is actually something to write into it.
  const record = useMemo(() => state || emptyFormationState(), [state]);

  const dimensions = useMemo(() => dimensionStatus(record, today, intervalDays), [
    record,
    today,
    intervalDays,
  ]);
  const practices = useMemo(() => practiceRecency(record.practised), [record.practised]);
  const tally = useMemo(() => commitmentTally(record.conversations), [record.conversations]);
  const due = useMemo(() => conversationDue(record.conversations, today, intervalDays), [
    record.conversations,
    today,
    intervalDays,
  ]);
  const open = useMemo(() => openCommitment(record.conversations), [record.conversations]);

  const practisedToday = useMemo(() => new Set(practisedOn(record.practised, today)), [
    record.practised,
    today,
  ]);

  // Both self-implicating fields, or nothing (concept §4.2). The gate is there
  // because an entry with only "who" and "what I said" is a diary entry, and
  // self-implication is the method's whole active ingredient (§2.2).
  const encounterComplete =
    !!encounterDraft.said.trim() &&
    !!encounterDraft.felt.trim() &&
    !!encounterDraft.differently.trim();

  const handleSaveNote = () => {
    if (!canWrite || !noteDraft) return;
    persist(setDimensionNote(record, noteDraft.key, noteDraft.text));
    setNoteDraft(null);
  };

  const handleLogEncounter = () => {
    if (!canWrite || !encounterComplete) return;
    persist(addEncounter(record, { ...encounterDraft, today }));
    setEncounterDraft(EMPTY_ENCOUNTER);
  };

  const handleLogObservation = () => {
    if (!canWrite || !observationDraft.what.trim()) return;
    persist(addObservation(record, { ...observationDraft, today }));
    setObservationDraft(EMPTY_OBSERVATION);
  };

  const handleLogPractice = (key) => {
    if (!canWrite) return;
    persist(addPractice(record, { key, today }));
  };

  const handleLogConversation = () => {
    if (!canWrite || !conversationDraft.brought.trim()) return;
    persist(addConversation(record, { ...conversationDraft, today }));
    setConversationDraft(EMPTY_CONVERSATION);
  };

  const header = (
    <div className="formation__header">
      <h3 className="formation__title">Pastorale Ausbildung</h3>
      <div className="formation__header__actions">
        <button className="formation__header__action" onClick={() => setReloadToken((n) => n + 1)}>
          <i className="fas fa-sync-alt" /> Refresh
        </button>
        <Link className="formation__header__action" to="/dashboard/contract">
          <i className="fas fa-file-signature" /> Contract
        </Link>
        <Link className="formation__header__action" to="/dashboard/week">
          <i className="fas fa-chart-line" /> This week
        </Link>
        <Link className="formation__header__action" to="/dashboard/focus">
          <i className="fas fa-bullseye" /> Focus
        </Link>
        <Link className="formation__header__action" to="/dashboard/team">
          <i className="fas fa-users" /> Team
        </Link>
        <Link className="formation__header__action" to="/dashboard/review">
          <i className="fas fa-moon" /> Review
        </Link>
        <Link className="formation__header__action" to="/dashboard">
          <i className="fas fa-th-large" /> Dashboard
        </Link>
      </div>
    </div>
  );

  if (!loaded) {
    return (
      <div className="formation-container">
        {header}
        <div className="formation__loading">
          <i className="fas fa-spinner fa-spin" /> Reading {path}...
        </div>
      </div>
    );
  }

  return (
    <div className="formation-container">
      {header}

      {!!loadError && <div className="formation__error">{loadError}</div>}
      {!!saveError && <div className="formation__error">{saveError}</div>}

      <div className="formation__intro">
        Your own formation, written by you alone. Nothing on this page scores you — not a dimension,
        not a behaviour, not a total. The evidence is against it: physicians self-assess worst when
        most confident (Davis et al. 2006), and a Vorbild is something observers confer, never
        something a candidate awards themselves (Cruess et al. 2008).
        {!state && (
          <>
            {' '}
            Nothing is written to <code>{path}</code> yet — the first entry below creates it.
          </>
        )}
      </div>

      {/* ── §4.1 The four dimensions ───────────────────────────────────────── */}

      <div className="formation__section-title">
        <span>The four dimensions</span>
        <span className="formation__section-title__note">recency and balance, never a score</span>
      </div>

      <div className="formation__hint">
        The 2016 <em>Ratio Fundamentalis</em> asks that these four be seen as one journey rather
        than four subjects passed separately — so there are no bars here and nothing adds up. What
        the rows show is negative space: a dimension nothing has been written under for{' '}
        {due.intervalDays} days is marked cold. That is a fact about a calendar, not a verdict.
      </div>

      <div className="formation__dimensions">
        {dimensions.map((dimension) => (
          <div
            className={`formation__dimension${dimension.cold ? ' formation__dimension--cold' : ''}`}
            key={dimension.key}
          >
            <div className="formation__dimension__head">
              <span className="formation__dimension__label">{dimension.label}</span>
              <span className="formation__dimension__when">
                {dimension.lastTouched === null
                  ? 'never'
                  : dimension.daysSince === 0
                  ? 'today'
                  : `${dimension.daysSince} days ago`}
                {dimension.encounters > 0 && ` · ${dimension.encounters} logged`}
              </span>
            </div>

            <div className="formation__dimension__gloss">{dimension.gloss}</div>

            {noteDraft && noteDraft.key === dimension.key ? (
              <div className="formation__inline-form">
                <input
                  className="textfield"
                  type="text"
                  value={noteDraft.text}
                  autoFocus
                  placeholder="What I am working on here, one line"
                  onChange={(event) => setNoteDraft({ ...noteDraft, text: event.target.value })}
                  onKeyDown={(event) => event.key === 'Enter' && handleSaveNote()}
                />
                <button className="formation__action" onClick={handleSaveNote}>
                  Save
                </button>
                <button className="btn btn-passive" onClick={() => setNoteDraft(null)}>
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className={`formation__dimension__note${
                  dimension.note ? '' : ' formation__dimension__note--empty'
                }`}
                disabled={!canWrite}
                onClick={() => setNoteDraft({ key: dimension.key, text: dimension.note })}
              >
                {dimension.note || 'one line: what I am working on here'}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* ── §4.2 The encounter ─────────────────────────────────────────────── */}

      <div className="formation__section-title">
        <span>The encounter</span>
        <span className="formation__section-title__note">{record.encounters.length} written</span>
      </div>

      <div className="formation__hint">
        A verbatim, in the Klinische Seelsorgeausbildung sense: your own words, as close to
        word-for-word as memory allows. <strong>Initials or a pseudonym only</strong> — this file
        syncs, and what was confided to you is not what is being recorded here. What is being
        recorded is you. Nothing from a sacramental confession belongs in this file at all.
      </div>

      {canWrite && (
        <div className="formation__form">
          <div className="formation__form__row">
            <input
              className="textfield formation__who"
              type="text"
              value={encounterDraft.who}
              placeholder="Initials, e.g. M.B."
              onChange={(event) =>
                setEncounterDraft({ ...encounterDraft, who: event.target.value })
              }
            />
            <div className="formation__choice">
              {DIMENSIONS.map((dimension) => (
                <button
                  className={`formation__choice__option${
                    encounterDraft.dimension === dimension.key
                      ? ' formation__choice__option--chosen'
                      : ''
                  }`}
                  key={dimension.key}
                  onClick={() =>
                    setEncounterDraft({
                      ...encounterDraft,
                      dimension: encounterDraft.dimension === dimension.key ? null : dimension.key,
                    })
                  }
                >
                  {dimension.label}
                </button>
              ))}
            </div>
          </div>

          <input
            className="textfield"
            type="text"
            value={encounterDraft.said}
            placeholder="What I said — my own words, as near verbatim as I can"
            onChange={(event) => setEncounterDraft({ ...encounterDraft, said: event.target.value })}
          />
          <input
            className="textfield"
            type="text"
            value={encounterDraft.felt}
            placeholder="What I felt while saying it — required"
            onChange={(event) => setEncounterDraft({ ...encounterDraft, felt: event.target.value })}
          />
          <input
            className="textfield"
            type="text"
            value={encounterDraft.differently}
            placeholder="What I would do differently — required"
            onChange={(event) =>
              setEncounterDraft({ ...encounterDraft, differently: event.target.value })
            }
          />
          <div className="formation__form__row">
            <input
              className="textfield"
              type="text"
              value={encounterDraft.avoided}
              placeholder="What I avoided saying — optional, and usually the valuable one"
              onChange={(event) =>
                setEncounterDraft({ ...encounterDraft, avoided: event.target.value })
              }
            />
            <button
              className="formation__action"
              disabled={!encounterComplete}
              onClick={handleLogEncounter}
            >
              Write it down
            </button>
          </div>
        </div>
      )}

      {record.encounters.length ? (
        <ul className="formation__log">
          {record.encounters
            .slice(-LOG_SHOWN)
            .reverse()
            .map((entry) => (
              <li className="formation__log__entry" key={`${entry.date}-${entry.said}`}>
                <div className="formation__log__head">
                  <span className="formation__log__date">{entry.date}</span>
                  {!!entry.who && <span className="formation__log__who">{entry.who}</span>}
                  {!!entry.dimension && (
                    <span className="formation__badge">
                      {(DIMENSIONS.find((d) => d.key === entry.dimension) || {}).label}
                    </span>
                  )}
                </div>
                <div className="formation__log__said">{entry.said}</div>
                {!!entry.felt && (
                  <div className="formation__log__field">
                    <span>felt</span> {entry.felt}
                  </div>
                )}
                {!!entry.differently && (
                  <div className="formation__log__field">
                    <span>differently</span> {entry.differently}
                  </div>
                )}
                {!!entry.avoided && (
                  <div className="formation__log__field">
                    <span>avoided</span> {entry.avoided}
                  </div>
                )}
              </li>
            ))}
        </ul>
      ) : (
        <div className="formation__empty">Nothing written yet.</div>
      )}

      {/* ── §4.3 Vorbild: two columns that never add up ─────────────────────── */}

      <div className="formation__section-title">
        <span>Vorbild</span>
        <span className="formation__section-title__note">two columns, never a sum</span>
      </div>

      <div className="formation__hint">
        These do not add up, and that is the design. The left is the only evidence here that bears
        on whether anyone actually sees you that way; the right is what you did. A full right column
        and an empty left one means you have been practising and nobody has said anything — which is
        information, and not the same as failure.
      </div>

      <div className="formation__columns">
        <div className="formation__column">
          <div className="formation__column__title">What others actually said or did</div>
          <div className="formation__column__hint">
            Only what was really said or done — never what you think someone must have thought.
            Feedback that lands on the person rather than the act is the third of it that makes
            things worse (Kluger &amp; DeNisi 1996), and “you’re a natural” is that kind of remark
            just as much as its opposite.
          </div>

          {canWrite && (
            <div className="formation__form">
              <div className="formation__form__row">
                <input
                  className="textfield formation__who"
                  type="text"
                  value={observationDraft.who}
                  placeholder="Who, initials"
                  onChange={(event) =>
                    setObservationDraft({ ...observationDraft, who: event.target.value })
                  }
                />
                <button
                  className="formation__action"
                  disabled={!observationDraft.what.trim()}
                  onClick={handleLogObservation}
                >
                  Log it
                </button>
              </div>
              <input
                className="textfield"
                type="text"
                value={observationDraft.what}
                placeholder="What they said, as close to their words as you can"
                onChange={(event) =>
                  setObservationDraft({ ...observationDraft, what: event.target.value })
                }
                onKeyDown={(event) => event.key === 'Enter' && handleLogObservation()}
              />
            </div>
          )}

          {record.observed.length ? (
            <ul className="formation__log">
              {record.observed
                .slice(-LOG_SHOWN)
                .reverse()
                .map((entry) => (
                  <li className="formation__log__entry" key={`${entry.date}-${entry.what}`}>
                    <div className="formation__log__head">
                      <span className="formation__log__date">{entry.date}</span>
                      {!!entry.who && <span className="formation__log__who">{entry.who}</span>}
                    </div>
                    <div className="formation__log__said">{entry.what}</div>
                  </li>
                ))}
            </ul>
          ) : (
            <div className="formation__empty">Nothing said yet — or nothing written down.</div>
          )}
        </div>

        <div className="formation__column">
          <div className="formation__column__title">What I practised</div>
          <div className="formation__column__hint">
            The behaviours the role-model literature actually distinguishes — time given, the
            relationship, teaching (Wright et al. 1998). Dates, not counts: a tally of your own good
            conduct is the licensing machine §2.5 warns about.
          </div>

          <div className="formation__practices">
            {practices.map((practice) => (
              <div className="formation__practice" key={practice.key}>
                <button
                  className="formation__practice__log"
                  disabled={!canWrite || practisedToday.has(practice.key)}
                  title="Logs this for today. Logging it twice in a day changes nothing."
                  onClick={() => handleLogPractice(practice.key)}
                >
                  <i className="fas fa-plus" />
                </button>
                <div className="formation__practice__body">
                  <div className="formation__practice__label">{practice.label}</div>
                  {!!practice.why && <div className="formation__practice__why">{practice.why}</div>}
                </div>
                <span className="formation__practice__when">
                  {practice.lastDate === null ? 'never' : practice.lastDate}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── §4.4 The formation conversation ────────────────────────────────── */}

      <div className="formation__section-title">
        <span>The formation conversation</span>
        <span className="formation__section-title__note">every {due.intervalDays} days</span>
      </div>

      <div className={`formation__conversation${due.due ? ' formation__conversation--due' : ''}`}>
        <div className="formation__conversation__status">
          {due.lastDate === null
            ? 'None logged yet. Ausbildungsgespräch, Mentorat, Supervision — whatever your programme calls the conversation with someone allowed to tell you the truth.'
            : due.due
            ? `Last one ${due.lastDate}, ${due.daysSince} days ago. Time for the next.`
            : `Last one ${due.lastDate}, ${due.daysSince} days ago. Next in ${
                due.intervalDays - due.daysSince
              } days.`}
        </div>

        {!!open && (
          <div className="formation__conversation__open">
            Last time you committed to: <strong>{open.committed}</strong>
          </div>
        )}

        {tally.total > 0 && (
          <div className="formation__numbers">
            <strong>{tally.kept}</strong> kept · <strong>{tally.partly}</strong> partly ·{' '}
            <strong>{tally.notKept}</strong> not kept
            {tally.unanswered > 0 && (
              <>
                {' '}
                · <strong>{tally.unanswered}</strong> never answered
              </>
            )}
            <span className="formation__numbers__note"> — a count of promises, not of you</span>
          </div>
        )}

        {canWrite && (
          <div className="formation__form">
            <input
              className="textfield"
              type="text"
              value={conversationDraft.brought}
              placeholder="What I brought to it"
              onChange={(event) =>
                setConversationDraft({ ...conversationDraft, brought: event.target.value })
              }
            />
            <input
              className="textfield"
              type="text"
              value={conversationDraft.named}
              placeholder="What they named — their words, not how well I think it went"
              onChange={(event) =>
                setConversationDraft({ ...conversationDraft, named: event.target.value })
              }
            />
            <input
              className="textfield"
              type="text"
              value={conversationDraft.committed}
              placeholder="What I committed to before the next one"
              onChange={(event) =>
                setConversationDraft({ ...conversationDraft, committed: event.target.value })
              }
            />
            <div className="formation__form__row">
              <div className="formation__choice">
                <span className="formation__choice__prompt">
                  {open ? 'The last commitment was' : 'Previous commitment'}
                </span>
                {COMMITMENT_STATES.map((value) => (
                  <button
                    className={`formation__choice__option${
                      conversationDraft.kept === value ? ' formation__choice__option--chosen' : ''
                    }`}
                    key={value}
                    onClick={() =>
                      setConversationDraft({
                        ...conversationDraft,
                        kept: conversationDraft.kept === value ? null : value,
                      })
                    }
                  >
                    {COMMITMENT_LABELS[value]}
                  </button>
                ))}
              </div>
              <button
                className="formation__action"
                disabled={!conversationDraft.brought.trim()}
                onClick={handleLogConversation}
              >
                Log the conversation
              </button>
            </div>
          </div>
        )}

        {record.conversations.length ? (
          <ul className="formation__log">
            {record.conversations
              .slice(-LOG_SHOWN)
              .reverse()
              .map((entry) => (
                <li className="formation__log__entry" key={`${entry.date}-${entry.brought}`}>
                  <div className="formation__log__head">
                    <span className="formation__log__date">{entry.date}</span>
                    {!!entry.kept && (
                      <span className={`formation__badge formation__badge--${entry.kept}`}>
                        {COMMITMENT_LABELS[entry.kept]}
                      </span>
                    )}
                  </div>
                  <div className="formation__log__said">{entry.brought}</div>
                  {!!entry.named && (
                    <div className="formation__log__field">
                      <span>named</span> {entry.named}
                    </div>
                  )}
                  {!!entry.committed && (
                    <div className="formation__log__field">
                      <span>committed</span> {entry.committed}
                    </div>
                  )}
                </li>
              ))}
          </ul>
        ) : (
          <div className="formation__empty">Nothing logged yet.</div>
        )}
      </div>

      {/* ── §4.5 What keeps this honest ────────────────────────────────────── */}

      <div className="formation__section-title">
        <span>What keeps this honest</span>
        <span className="formation__section-title__note">a reminder, not a form</span>
      </div>

      <div className="formation__cards">
        {HONESTY_CARDS.map((card) => (
          <div className="formation__card" key={card.text}>
            <div className="formation__card__text">{card.text}</div>
            <div className="formation__card__why">{card.why}</div>
            <div className="formation__card__strength">{card.strength}</div>
          </div>
        ))}
      </div>

      <div className="formation__footer">
        Stored as one <code>* Pastorale Ausbildung :pastoral-formation:</code> node in{' '}
        <code>{path}</code>, one commit per change. Initials only, and nothing from a sacramental
        confession. See <code>doc/pastoral-formation-concept.org</code> for what each section rests
        on — and for which of it is honestly thin.
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  syncBackendClient: state.syncBackend.get('client'),
  formationPath: state.base.get('formationPath') || DEFAULT_FORMATION_PATH,
  intervalDays:
    state.base.get('formationIntervalDays') === undefined
      ? DEFAULT_FORMATION_INTERVAL_DAYS
      : state.base.get('formationIntervalDays'),
});

export default connect(mapStateToProps)(FormationDashboard);
